// Service Worker registration and management

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOfflineReady?: () => void;
}

export function registerSW(config?: ServiceWorkerConfig) {
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(
      import.meta.env.VITE_PUBLIC_URL || '',
      window.location.href
    );
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${import.meta.env.VITE_PUBLIC_URL || ''}/sw.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('Service Worker: Ready in localhost mode');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: ServiceWorkerConfig) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      console.log('Service Worker: Registered successfully');

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log(
                'Service Worker: New content available, please refresh'
              );
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log('Service Worker: Content cached for offline use');
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
              if (config && config.onOfflineReady) {
                config.onOfflineReady();
              }
            }
          }
        };
      };
    })
    .catch(error => {
      console.error('Service Worker: Registration failed', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: ServiceWorkerConfig) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then(response => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'Service Worker: No internet connection, running in offline mode'
      );
    });
}

export function unregisterSW() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
        console.log('Service Worker: Unregistered');
      })
      .catch(error => {
        console.error('Service Worker: Unregistration failed', error);
      });
  }
}

// Utility to check if app is running offline
export function isAppOffline(): boolean {
  return !navigator.onLine;
}

// Utility to sync data when back online
export function syncWhenOnline(callback: () => void) {
  if (navigator.onLine) {
    callback();
  } else {
    const handleOnline = () => {
      callback();
      window.removeEventListener('online', handleOnline);
    };
    window.addEventListener('online', handleOnline);
  }
}

// Background sync for test answers
export function scheduleBackgroundSync(tag: string, _data?: any) {
  if (
    'serviceWorker' in navigator &&
    'sync' in window.ServiceWorkerRegistration.prototype
  ) {
    navigator.serviceWorker.ready
      .then(registration => {
        return (registration as any).sync.register(tag);
      })
      .catch(error => {
        console.error('Background sync registration failed:', error);
        // Fallback: try to sync immediately
        if (navigator.onLine) {
          syncWhenOnline(() => {
            console.log('Fallback sync executed');
          });
        }
      });
  }
}

// Cache management utilities
export async function clearAppCache() {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
    console.log('All caches cleared');
  }
}

export async function getCacheSize(): Promise<number> {
  if (
    'caches' in window &&
    'storage' in navigator &&
    'estimate' in navigator.storage
  ) {
    const estimate = await navigator.storage.estimate();
    return estimate.usage || 0;
  }
  return 0;
}

// Performance monitoring
export function measureCachePerformance(
  url: string
): Promise<{ fromCache: boolean; duration: number }> {
  const startTime = performance.now();

  return fetch(url).then(response => {
    const endTime = performance.now();
    const duration = endTime - startTime;
    const fromCache = response.headers.get('cache-control') !== null;

    return { fromCache, duration };
  });
}
